/*
 * @Author: Teemo Ding
 * @Date: 2021-07-09 11:07:51
 * @LastEditors: Json Chen
 * @LastEditTime: 2022-06-02 17:33:53
 * @FilePath: /unifyxp-v2-store-portal/src/pages/Settings/Taxes/TaxListPage/index.tsx
 */

import { NoDataPage } from '@/src/components';
import { Skeleton } from '@/src/components/Antd';
import GlobalTable from '@/src/constrains/BulkEditorTable/BulkEditorTable';
import Layout from '@/src/layout/EditorPageLayout/EditorPageLayout';
import TopLeft from '@/src/layout/EditorPageLayout/GoBackAndBreadCrumb/GoBackAndBreadCrumb';
import { useStoreCountries } from '@/src/pages/Settings/Components/SelectCountryArea';
import {
  deleteTax,
  initStoreTaxes,
  searchStoreTaxes,
} from '@/src/request/settings/tax';
import {
  isUserValid,
  storeResourceNameGenerator,
} from '@/src/tools/getUserInfo';
import {
  EPermissionSource,
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
} from '@/src/types/common/resources';
import { message } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { FilterType } from 'src/constrains/Query/Query';
import ListPageLayout from 'src/layout/ListPageLayout/ListPageLayout';
import { defaultPageSize } from 'src/tools/constant';
import { useAfterDidMountEffect } from 'src/tools/customHooks';
import { modal } from 'src/utils/Modal';
import { breadcrumbData, columns } from './config';
import css from './css.module.less';
// import { queryConvert } from 'src/tools/queryConvert';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';
import {
  deleteSearch,
  eachOnFilter,
  filterClose,
  onFilter,
  saveSearchList,
} from './slice';

interface Sort {
  columnKey?: string;
  field?: string;
  order?: string;
}

const permissions = () => {
  const view_all_tax_rule_listing_page = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsTaxes,
        operation: EnumOperation.view_all_tax_rule_listing_page,
      }),
    ],
  });
  const add_edit_tax_rule = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsTaxes,
        operation: EnumOperation.add_edit_tax_rule,
      }),
    ],
  });
  const delete_tax_rule = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsTaxes,
        operation: EnumOperation.delete_tax_rule,
      }),
    ],
  });
  const view_tax_rule_details = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsTaxes,
        operation: EnumOperation.view_tax_rule_details,
      }),
    ],
  });
  return {
    view_all_tax_rule_listing_page,
    add_edit_tax_rule,
    delete_tax_rule,
    view_tax_rule_details,
  };
};
const TaxListPage = () => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const {
    view_all_tax_rule_listing_page,
    add_edit_tax_rule,
    delete_tax_rule,
    view_tax_rule_details,
  } = permissions();
  const state = useSelector((state: any) => state.storeSettingTaxList);
  const { list = [] } = useStoreCountries();
  const _d = useDispatch();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [sort, setSort] = useState<Sort>({});
  /* 分页 */
  const [current, setCurrent] = useState(0);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const [taxes, setTaxes] = useState([]);

  const { tabsActive, tableSettings, filter, searchList } = state;
  //初始化页面数据
  useEffect(() => {
    if (view_all_tax_rule_listing_page) {
      if (!loading) {
        setLoading(true);
      }
      initStoreTaxes(current * pageSize, pageSize).then((res) => {
        setLoading(false);
        setTaxes(res?.storeTaxSettings.results ?? []);
        const filterData = (res?.countries || []).map((o) => ({
          key: o.code,
          name: o.name,
        }));
        setTotal(res?.storeTaxSettings.totalCount);
        setFilters([
          {
            type: FilterType.checkBox,
            name: 'storeTaxes.taxListPage.countryArea',
            dataKey: 'countryCode',
            data: filterData,
          },
        ]);
      });
    } else {
      setLoading(false);
    }
  }, []);

  //构建搜索条件
  const buildRequestData = () => {
    const { countryCode: countryCodeData = [] } = filter;
    const countryCodes = countryCodeData.map((productType) => productType.key);
    const requestData = {
      countryCodes,
      searchText: searchList,
      ...(sort?.order
        ? {
            sortBy: {
              field: sort?.field,
              ascent: sort?.order == 'ascend' ? true : false,
            },
          }
        : {}),
    };
    return requestData;
  };

  //搜索
  const getTax = () => {
    if (!tableLoading) {
      setTableLoading(true);
    }
    const data = buildRequestData();
    searchStoreTaxes(current * pageSize, pageSize, data).then((res) => {
      const { totalCount, results } = res;
      setTotal(totalCount);
      setTaxes(results ?? []);
      setTableLoading(false);
    });
  };

  const goBackToList = () => {
    history.push({
      pathname: '/settings',
    });
  };

  useAfterDidMountEffect(() => getTax(), [filter, searchList, current, sort]);
  useAfterDidMountEffect(() => {
    if (!current) {
      getTax();
    } else {
      setCurrent(0);
    }
  }, [pageSize]);

  const topLeft = (
    <TopLeft
      goBack={goBackToList}
      breadcrumbData={breadcrumbData({ getI18n })}
    />
  );

  //清除table状态
  const cleanState = () => {
    setCurrent(0);
  };

  const addFun = () => {
    history.push('/settings/tax/edit');
  };
  const tabChange = () => {
    console.log('tab change');
  };
  //删除某个tax
  const deleteFun = (record) => {
    modal.error({
      title: getI18n('StoreTaxes'),
      content: getI18n('storeTaxes.taxListPage.deleteTaxRulesContent'),
      onCancelText: getI18n('general.cancel'),
      onCancel: () => {
        console.log('close');
      },
      onOk: () =>
        deleteTax(record.uuid).then(() => {
          getTax();
          message.success(
            getI18n('storeTaxes.taxListPage.deletedsuccessfully'),
          );
        }),
      onOkText: getI18n('general.delete'),
    });
  };

  const editFun = (record) => {
    history.push(`/settings/tax/edit/${record.uuid}`);
  };

  const eachOnFilterClick = (data) => {
    _d(eachOnFilter(data));
    cleanState();
  };
  const onSearch = (value) => {
    _d(saveSearchList(value));
    cleanState();
  };
  const onTagClose = (index) => {
    _d(deleteSearch(index));
    cleanState();
  };
  const filterCloseClick = (data) => {
    _d(filterClose(data));
    cleanState();
  };

  const pageLayoutProps = {
    title: getI18n('storeTaxes.taxListPage.tax'),
    addText: add_edit_tax_rule
      ? getI18n('storeTaxes.taxListPage.addTaxRule')
      : '',
    addFun,
    tabChange,
    activeKey: tabsActive,
    tabsConfig: [
      //定义tabs列表
      { name: getI18n('general.all'), key: '' },
    ],
  };

  const queryProps = {
    searchList,
    deleteSearch,
    onFilter,
    eachOnFilterClick,

    filter,
    // activeChange,
    filterList: filters.map((item) => ({ ...item, name: getI18n(item.name) })),
    state,

    onSearch,
    onPress: onSearch,
    onTagClose,
    filterCloseClick,
  };

  //分页配置
  const paginationProps = {
    defaultPageSize: pageSize,
    showQuickJumper: true,
  };

  const Child = [
    {
      child: (
        <ListPageLayout {...pageLayoutProps} tabChange={tabChange}>
          <GlobalTable
            id='taxListTable'
            // onTableChange={setProducts}
            queryProps={queryProps}
            dataSource={taxes}
            columns={columns(
              deleteFun,
              editFun,
              list,
              !view_tax_rule_details,
              !delete_tax_rule,
              { getI18n },
            )}
            rowKey='uuid'
            pagination={paginationProps}
            current={current}
            setCurrent={setCurrent}
            pageSize={pageSize}
            onChange={(page, fil, sorter) => {
              setSort(sorter);
            }}
            setPageSize={setPageSize}
            total={total}
            hideHeader
            hideRowSelection
            loading={tableLoading}
            hidePagination={total == 0}
            tableSettings={tableSettings}
          />
        </ListPageLayout>
      ),
    },
  ];
  return (
    <Skeleton loading={loading} active>
      {taxes.length == 0 && (filter?.countryCode || []).length == 0 ? (
        <div>
          <div className={css.topStickContainer}>
            <div className={css.topStickLeft}>{topLeft}</div>
          </div>
          <div className={css.noDataPage}>
            <NoDataPage
              title={getI18n('storeTaxes.taxListPage.addtaxrulestostore')}
              introduce={getI18n(
                'storeTaxes.taxListPage.addmanagetaxrulesinstore',
              )}
              buttonTitle={getI18n('storeTaxes.taxListPage.addTaxRules')}
              onClick={addFun}
              disabled={!add_edit_tax_rule}
            />
          </div>
        </div>
      ) : (
        <Layout elements={Child} topLeft={topLeft}></Layout>
      )}
    </Skeleton>
  );
};

export default TaxListPage;
